import { useState } from 'react';
import { Button } from './ui/button';
import { RefreshCw } from 'lucide-react';

const hungerLevels = [
  { min: 1, max: 2, emoji: "😐", label: "Pas faim", color: "bg-blue-500", advice: "Vous n'avez pas vraiment faim. Attendez que la sensation se manifeste vraiment avant de manger." },
  { min: 3, max: 4, emoji: "🙂", label: "Légère faim", color: "bg-green-500", advice: "Une petite faim commence à pointer. C'est bientôt le bon moment, mais vous pouvez encore attendre." },
  { min: 5, max: 6, emoji: "😊", label: "Faim modérée", color: "bg-sage-500", advice: "C'est le moment idéal pour manger ! Vous avez assez faim pour apprécier votre repas sans vous jeter dessus." },
  { min: 7, max: 8, emoji: "😋", label: "Vraie faim", color: "bg-amber-500", advice: "Vous avez bien faim ! Passez à table, mais attention à ne pas manger trop vite." },
  { min: 9, max: 10, emoji: "🤤", label: "Très faim !", color: "bg-declic", advice: "Attention, risque de manger trop vite ! Prenez 3 grandes respirations avant de commencer et posez vos couverts entre chaque bouchée." }
];

const getHungerLevel = (value) => {
  return hungerLevels.find(level => value >= level.min && value <= level.max);
};

export const HungerGauge = () => {
  const [value, setValue] = useState(5);
  const [showResult, setShowResult] = useState(false);
  
  const level = getHungerLevel(value);

  const handleValidate = () => {
    setShowResult(true);
  };

  const handleReset = () => {
    setValue(5);
    setShowResult(false);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium text-sage-800 mb-2">
          Évaluez votre niveau de faim
        </h3>
        <p className="text-sage-500 text-sm">
          Avant de manger, prenez un moment pour vous connecter à vos sensations
        </p>
      </div>

      {/* Gauge Display */}
      <div className="py-8">
        <div className="text-center mb-6">
          <span className="text-6xl mb-2 block">{level.emoji}</span>
          <span className={`inline-block px-4 py-1 rounded-full text-white text-sm font-medium ${level.color}`}>
            {level.label}
          </span>
        </div>

        {/* Slider */}
        <div className="relative px-2">
          <input
            type="range"
            min="1"
            max="10"
            value={value}
            onChange={(e) => {
              setValue(parseInt(e.target.value));
              setShowResult(false);
            }}
            className="w-full h-3 bg-gradient-to-r from-blue-400 via-green-400 via-amber-400 to-red-400 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-8
              [&::-webkit-slider-thumb]:h-8
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-white
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:border-4
              [&::-webkit-slider-thumb]:border-sage-400
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-moz-range-thumb]:w-8
              [&::-moz-range-thumb]:h-8
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-white
              [&::-moz-range-thumb]:shadow-lg
              [&::-moz-range-thumb]:border-4
              [&::-moz-range-thumb]:border-sage-400
              [&::-moz-range-thumb]:cursor-pointer"
          />
          
          {/* Scale numbers */}
          <div className="flex justify-between mt-2 text-xs text-sage-400">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <span key={n} className={value === n ? 'text-sage-700 font-bold' : ''}>{n}</span>
            ))}
          </div>
        </div>

        {/* Value display */}
        <div className="text-center mt-4">
          <span className="text-4xl font-light text-sage-700">{value}</span>
          <span className="text-sage-400 text-lg">/10</span>
        </div>
      </div>

      {/* Result or Validate Button */}
      {showResult ? (
        <div className="space-y-4">
          <div className={`${level.color.replace('bg-', 'bg-opacity-10 bg-')} rounded-xl p-4 border-l-4 ${level.color.replace('bg-', 'border-')}`}>
            <p className="text-sage-700">{level.advice}</p>
          </div>
          
          <Button
            onClick={handleReset}
            variant="outline"
            className="w-full h-12 rounded-xl border-sage-300 text-sage-600 hover:bg-sage-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Nouvelle évaluation
          </Button>
        </div>
      ) : (
        <Button
          onClick={handleValidate}
          className="w-full h-12 bg-sage-500 hover:bg-sage-600 text-white rounded-xl"
        >
          Voir le conseil
        </Button>
      )}

      {/* Tips */}
      <div className="bg-sage-50 rounded-xl p-4 text-sm text-sage-600">
        <p className="font-medium mb-2">💡 Astuce</p>
        <p>L'idéal est de manger quand vous êtes entre <strong>5 et 7</strong> sur l'échelle. En dessous, vous n'avez pas vraiment faim. Au-dessus, vous risquez de manger trop vite !</p>
      </div>
    </div>
  );
};

export default HungerGauge;
